import { createContext, useContext, useMemo, useRef, type ReactNode, type RefObject } from "react";
import { useAtomMount, useAtomValue } from "@effect/atom-react";
import { Effect } from "effect";
import { Atom, type AsyncResult } from "effect/unstable/reactivity";
import type { FrontendRoot } from "../state/root-owner.ts";
import { coreBindings, type Keybinding } from "./model.ts";
import { KeybindRegistry } from "./service.ts";
import { PaletteShortcuts } from "./shortcuts.ts";
import { MenuEscapes } from "./menu-escapes.ts";
import { ModalFocusSessions, type ModalFocusSession } from "./modal-focus-service.ts";

export interface KeybindRoot {
  readonly atoms: FrontendRoot<KeybindRegistry | PaletteShortcuts | MenuEscapes | ModalFocusSessions>["atoms"];
  readonly keybinds: import("effect").Context.Service.Shape<typeof KeybindRegistry>;
}
const KeybindContext = createContext<KeybindRoot | undefined>(undefined);
export const KeybindProvider = ({ value, children }: { readonly value: KeybindRoot; readonly children: ReactNode }) => <KeybindContext.Provider value={value}>{children}</KeybindContext.Provider>;
export function useModalFocus(open: boolean, target: RefObject<HTMLElement | null>, onClose: () => void): void {
  const root = useContext(KeybindContext), state = useRef({ open, onClose }), previous = useRef<HTMLElement | null>(null);state.current = { open, onClose };
  const identity = useMemo(() => ({ root }), [root]), current = useRef(identity);current.current = identity;
  const opened = useMemo<Atom.Atom<AsyncResult.AsyncResult<{ readonly identity: object; readonly session: ModalFocusSession } | undefined>>>(() => root ? root.atoms.atom(Effect.gen(function* () {
    const service = yield* ModalFocusSessions;
    const session = yield* service.open({ isOpen: () => current.current !== identity || state.current.open, remember: element => { if (current.current === identity) previous.current = element; }, previous: () => current.current === identity ? previous.current : null, close: () => { if (current.current === identity) state.current.onClose(); } });
    return { identity, session };
  })).pipe(Atom.setIdleTTL(0)) : Atom.make(Effect.succeed(undefined)), [root, identity]);
  const result = useAtomValue(opened), descriptor = result._tag === "Success" ? result.value : undefined, session = descriptor?.identity === identity ? descriptor.session : undefined;
  const receiving = useMemo(() => root && session ? root.atoms.atom(Effect.suspend(() => session.receive(open, target.current))).pipe(Atom.setIdleTTL(0)) : Atom.make(Effect.void), [root, session, open, target]);
  useAtomMount(receiving);
}

export function useMenuEscape(menu: object | null, close: () => void): void {
  const root = useContext(KeybindContext), notify = useRef(close);notify.current = close;
  const identity = useMemo(() => ({ root, menu }), [root, menu]), current = useRef(identity);current.current = identity;
  const subscription = useMemo(() => root && menu ? root.atoms.atom(MenuEscapes.use(service => service.listen(Effect.sync(() => {
    if (current.current === identity) notify.current();
  })))).pipe(Atom.setIdleTTL(0)) : Atom.make(Effect.void), [root, menu, identity]);
  useAtomMount(subscription);
}

export function useKeybindings(): readonly Keybinding[] {
  const root = useContext(KeybindContext);
  const defaults = useMemo(() => Atom.make(Effect.succeed(coreBindings())), []);
  const state = useAtomValue(root?.keybinds.snapshot ?? defaults);
  return state._tag === "Success" ? state.value : [];
}
export function usePaletteShortcut(enabled: boolean, toggle: () => void): void {
  const root = useContext(KeybindContext), current = useRef(toggle);
  current.current = toggle;
  const contribution = useMemo(() => root && enabled ? root.atoms.atom(Effect.acquireRelease(
    Effect.gen(function* () { return yield* (yield* PaletteShortcuts).listen(() => current.current()); }),
    cleanup => cleanup
  ).pipe(Effect.asVoid)) : Atom.make(Effect.void), [root, enabled]);
  useAtomMount(contribution);
}
